import { type ReactNode, useId } from 'react'
import { cx } from './cx.ts'
import { DetailTip } from './detail-tip.tsx'

export type CheckboxProps = {
  name?: string | undefined
  checked: boolean
  onChange: (checked: boolean) => void
  /** Treść zgody (etykieta). */
  children: ReactNode
  /** Pełna klauzula w dymku „[szczegóły]” (jak HubSpot na step.tutlo.com.pl). */
  detail?: ReactNode | undefined
  /** Czerwona gwiazdka po etykiecie (pole wymagane). */
  required?: boolean
  disabled?: boolean
  error?: string | undefined
}

/**
 * Zgoda z checkboxem 1:1 ze step.tutlo.com.pl (HubSpot `hs-form-booleancheckbox`): kwadrat 20 px, radius 6,
 * hover = violet ramka, zaznaczony = violet tło + biały ptaszek, tekst 13 px szary. Natywny input (sr-only)
 * + dwie etykiety (kwadrat i tekst), bo „[szczegóły]” to przycisk i nie może siedzieć w <label>.
 * Ptaszek jest zawsze w DOM: biały na białym = niewidoczny, `peer-checked:text-white` na violet = widoczny.
 * Wiersz jest `relative`, żeby dymek szczegółów rozciągał się nad całą zgodą.
 */
export function Checkbox({
  name,
  checked,
  onChange,
  children,
  detail,
  required = false,
  disabled = false,
  error,
}: CheckboxProps) {
  const id = useId()
  const errorId = `${id}-error`
  return (
    <div className="relative flex flex-col gap-[6px]">
      <div className="flex items-start gap-[10px]">
        <input
          id={id}
          type="checkbox"
          name={name}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="peer sr-only"
        />
        <label
          htmlFor={id}
          aria-hidden="true"
          className={cx(
            'mt-px flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-[6px] border-[1.5px] bg-bg-card text-bg-card transition-colors duration-150 hover:border-selected peer-checked:border-selected peer-checked:bg-selected peer-checked:text-white peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-selected peer-disabled:cursor-default peer-disabled:opacity-50',
            error ? 'border-error' : 'border-border',
          )}
        >
          <svg
            viewBox="0 0 24 24"
            className="size-[13px]"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </label>
        <span className="text-[13px] leading-[1.45] text-text-secondary">
          <label htmlFor={id} className="cursor-pointer">
            {children}
          </label>
          {detail ? (
            <>
              {' '}
              {/* Muted like the surrounding consent text (Robert, 2026-09-12), not the violet HubSpot link. */}
              <DetailTip className="text-text-secondary/70">{detail}</DetailTip>
            </>
          ) : null}
          {required ? (
            <>
              {' '}
              <span aria-hidden="true" className="text-error">
                *
              </span>
            </>
          ) : null}
        </span>
      </div>
      {error ? (
        <span id={errorId} className="text-label text-error">
          {error}
        </span>
      ) : null}
    </div>
  )
}
